/**
 * Demo Mode Utilities
 * 
 * IMPORTANT: Demo mode provides READ-ONLY access to a demo workspace.
 * 
 * This module provides isolated demo mode functionality that allows users to
 * explore the application without authentication. All write operations MUST
 * be blocked when demo mode is active.
 * 
 * Key principles:
 * - Demo mode is READ-ONLY - no database writes, no storage uploads/deletes
 * - Demo mode uses sessionStorage - cleared when browser session ends
 * - Demo mode is isolated - uses dedicated demo workspace ID
 * - All write operations MUST check isDemoMode() and block if true
 * 
 * Security:
 * - Demo workspace ID is hardcoded and allows public read access via Firestore rules
 * - All write operations must be blocked in demo mode (handled in UI components)
 * - Firestore rules prevent writes to demo workspace by unauthenticated users
 */

/**
 * Demo workspace ID - hardcoded workspace used for demo mode
 * This workspace allows public read access via Firestore security rules
 */
export const DEMO_WORKSPACE_ID = "q15r2mqzlCX2a6TPZ6Xy";

/**
 * Session storage key for demo mode flag
 */
const DEMO_MODE_KEY = "demoMode";

/**
 * Prefix for demo cache keys in sessionStorage
 * Used to store temporary data during demo mode (read-only)
 */
const DEMO_CACHE_PREFIX = "demo_cache_";

/**
 * Check if demo mode is currently active
 * 
 * @returns true if demo mode is active, false otherwise
 * 
 * NOTE: This should be checked at the start of ALL write operations
 * (create, update, delete, upload) to prevent data modifications.
 */
export const isDemoMode = (): boolean => {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(DEMO_MODE_KEY) === "true";
};

/**
 * Enable demo mode
 * 
 * Sets a flag in sessionStorage to indicate demo mode is active.
 * This flag is used throughout the app to block write operations.
 * 
 * NOTE: Demo mode is automatically cleared when browser session ends.
 */
export const enableDemoMode = (): void => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(DEMO_MODE_KEY, "true");
};

/**
 * Disable demo mode and clear all cache
 * 
 * Removes demo mode flag and cleans up any demo-related cache data.
 * Should be called when user logs in or explicitly exits demo mode.
 */
export const disableDemoMode = (): void => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(DEMO_MODE_KEY);
  clearDemoCache();
};

/**
 * Get demo workspace ID
 * 
 * @returns The hardcoded demo workspace ID
 * 
 * This workspace has special Firestore rules that allow public read access.
 */
export const getDemoWorkspaceId = (): string => {
  return DEMO_WORKSPACE_ID;
};

/**
 * Clear all demo cache data
 * 
 * Removes all sessionStorage items that were created during demo mode.
 * This ensures a clean state when demo mode is disabled.
 * 
 * NOTE: Currently, demo mode doesn't use cache (all data is read from database).
 * This function is kept for future use and cleanup.
 */
export const clearDemoCache = (): void => {
  if (typeof window === "undefined") return;
  
  // Clear all demo cache items
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(DEMO_CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => sessionStorage.removeItem(key));
};

/**
 * ============================================================================
 * DEMO CACHE FUNCTIONS
 * ============================================================================
 * 
 * NOTE: These cache functions are currently NOT USED in the application.
 * Demo mode reads all data directly from the database (read-only access).
 * 
 * These functions are kept for potential future use if we need to implement
 * client-side caching for demo mode. However, they should NOT be used for
 * write operations as demo mode is strictly read-only.
 */

/**
 * Get cache key for a collection
 * @private - internal function
 */
const getCacheKey = (collectionName: string): string => {
  return `${DEMO_CACHE_PREFIX}${collectionName}`;
};

/**
 * Get data from cache
 * 
 * NOTE: Currently unused - demo mode reads from database directly.
 * 
 * @param collectionName - Name of the collection to retrieve from cache
 * @returns Array of cached items, empty array if not in demo mode or cache miss
 */
export const getFromCache = <T>(collectionName: string): T[] => {
  if (!isDemoMode()) return [];
  if (typeof window === "undefined") return [];
  
  try {
    const cacheKey = getCacheKey(collectionName);
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached) as T[];
    }
  } catch (error) {
    console.error("Error reading from demo cache:", error);
  }
  return [];
};

/**
 * Save data to cache
 * 
 * NOTE: Currently unused - demo mode doesn't cache data.
 * This should NOT be used for write operations as demo mode is read-only.
 * 
 * @param collectionName - Name of the collection to save
 * @param data - Array of items to cache
 */
export const saveToCache = <T>(collectionName: string, data: T[]): void => {
  if (!isDemoMode()) return;
  if (typeof window === "undefined") return;
  
  try {
    const cacheKey = getCacheKey(collectionName);
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving to demo cache:", error);
  }
};

/**
 * Add item to cache
 * 
 * NOTE: Currently unused - demo mode doesn't support adding items.
 * This should NOT be used as demo mode is read-only.
 */
export const addToCache = <T extends { id: string }>(collectionName: string, item: T): void => {
  if (!isDemoMode()) return;
  
  const existing = getFromCache<T>(collectionName);
  existing.push(item);
  saveToCache(collectionName, existing);
};

/**
 * Update item in cache
 * 
 * NOTE: Currently unused - demo mode doesn't support updating items.
 * This should NOT be used as demo mode is read-only.
 */
export const updateInCache = <T extends { id: string }>(collectionName: string, itemId: string, updates: Partial<T>): void => {
  if (!isDemoMode()) return;
  
  const existing = getFromCache<T>(collectionName);
  const index = existing.findIndex(item => item.id === itemId);
  if (index !== -1) {
    existing[index] = { ...existing[index], ...updates } as T;
    saveToCache(collectionName, existing);
  }
};

/**
 * Remove item from cache
 * 
 * NOTE: Currently unused - demo mode doesn't support removing items.
 * This should NOT be used as demo mode is read-only.
 */
export const removeFromCache = (collectionName: string, itemId: string): void => {
  if (!isDemoMode()) return;
  
  const existing = getFromCache(collectionName);
  const filtered = existing.filter((item: any) => item.id !== itemId);
  saveToCache(collectionName, filtered);
};

/**
 * Merge cache data with database data
 * 
 * NOTE: Currently unused - demo mode doesn't use cache.
 * 
 * Cache data takes precedence (overwrites database data with same ID).
 * This should NOT be used for write operations as demo mode is read-only.
 */
export const mergeCacheWithDatabase = <T extends { id: string }>(
  cacheData: T[],
  databaseData: T[]
): T[] => {
  if (!isDemoMode()) return databaseData;
  
  // Start with database data
  const merged = [...databaseData];
  
  // Overwrite with cache data (cache takes precedence)
  cacheData.forEach(cacheItem => {
    const index = merged.findIndex(item => item.id === cacheItem.id);
    if (index !== -1) {
      merged[index] = cacheItem;
    } else {
      merged.push(cacheItem);
    }
  });
  
  return merged;
};
